import $ from 'jquery';
import _ from 'underscore';
import URI from 'urijs';
import { DeterministicWrapperView } from '@bingads-webui/deterministic-wrapper-view';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { showLabelsPopup, filterOnLabelClick, navigateToManagePage } from '@bingads-webui-campaign/labels';
import { showQualityScoreDetail } from '@bingads-webui-campaign/campaignui-common-columns';
import { DeliveryStatusPopupFactory, showRejectionReasons, registerCountryListEvent } from '@bingads-webui-campaign/delivery-status-popup-factory';
import { getEditorialReasonsTemplate } from '@bingads-webui-campaign/rsa-ad-customizer-attribute-column';
import { updateGridQuery, appendScopeParameter, setUpErrorHandling, handleShowRejectionReasonEnter, handleShowRejectionReasonLeave } from '@bingads-webui-campaign/summary-grid-helper';
import { pgridFactory } from '@bingads-webui-campaign/pgrid-factory';
import { PageReady } from '@bingads-webui/page-ready';
import state from '@bingads-webui-component-legacy/state/index';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';

import { getAdGroupBidLandscapeInitializer, getAdGroupBidLandscapeInitializerWebpack } from '../index';
import { hideCampaignManagedDateTooltip, renderCampaignManagedDateTooltip } from './tooltips/campaign-managed-date-tooltip';

const appConfig = globalContext.require('component/config/index');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-groups-tab');
const commonI18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ads-tab/common');
const { handleSortChange } = globalContext.require('campaignui/feature/bingadsgrid/service/state-manager-helper');
const legacyGlobalI18n = globalContext.require('$/i18n!legacy/global');
const CopyPaste = globalContext.require('module/view/bulkEdit/copyPaste');
const Hotkeys = globalContext.require('component/hotkeys/index');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');

const permissions = appConfig.get('Permissions');

export class AdGroupsGrid extends DeterministicWrapperView {
  viewFactory({
    deps,
    filterService,
    getGridConfig,
    getPlugins,
    preferenceConfig,
    router,
    scope,
    isForVNext = false,
    currentCampaign,
    pageName,
    preexecuteFlag = false,
  }) {
    this.instrumentation = instrumentation;
    this.gridRemoved = false;
    this.currentCampaign = currentCampaign;
    const gridConfig = getGridConfig(this);

    this.gridName = gridConfig.name;
    this.deps = deps;
    this.isForVNext = isForVNext;
    this.filterService = filterService;
    this.scope = scope;
    this.router = router;
    this.grid = pgridFactory({
      gridConfig,
      plugins: getPlugins(this),
      preferenceConfig,
    });

    this.rejectionPopupContainerSelector = '#rejectionContainer';
    this.popoverId = 'delivery-status-popup-callout';
    this.pageName = pageName;
    this.preexecuteFlag = preexecuteFlag;
    this.isMCCLabelEnabled = permissions ? permissions.IsAccountLabelsEnabled : false;

    if (!(permissions.dynamic && permissions.dynamic.AdGroupsPageDFMV2)) {
      // If DFMv2 is disabled, we need to add the error handler for dataSource
      setUpErrorHandling({
        i18n,
        scope,
        gridView: this,
        dataSource: gridConfig.dataSource,
      });
    }

    if (isForVNext) {
      this.filterService.on('filter-state-changed', () => {
        if (!this.gridRemoved) {
          const oldQuery = _.result(this.grid.gridView.get('query'), 'filter');
          const newQuery = _.result(this.filterService.getCombinedFilter(), 'query');
          // If filter has not changed, do not update grid query
          if (!_.isEqual(oldQuery, newQuery)) {
            updateGridQuery(
              this.grid.gridView,
              this.filterService.getCombinedFilter(), true
            );
          }
        }
      });
    }

    this.grid.gridView.on('willUpdate', (changes) => {
      handleSortChange(changes, this.grid.gridView, gridConfig.name);
    });

    this.grid.gridView.on('didUpdate', () => {
      this.gridDidUpdateHandler();
    });

    this.grid.gridView.once('didReload', () => {
      this.grid.gridView.once('didRedraw', () => {
        PageReady.pageIsReady(this.pageName);
      }, this);
    });

    this.grid.pagination.on('change:page-number', (pageNumber) => {
      this.setPageNumberToState(pageNumber);
    });

    if (!this.preexecuteFlag && permissions.IsCampaignAdGroupCopyPasteEnabled && this.grid.config.name === 'summary_tab_ad_group') {
      const bulkEditGridOptions = {
        filterService: this.filterService,
        gridView: this.grid.gridView,
        pgridPlugins: this.grid,
        isForVNext: this.isForVNext,
      };

      Hotkeys.on('copy', CopyPaste.copy.bind(CopyPaste, 'adgroup', bulkEditGridOptions));
      if (this.isForVNext) {
        Hotkeys.on('paste', this.grid.paste.onClick);
      } else {
        Hotkeys.on('paste', CopyPaste.paste.bind(CopyPaste, bulkEditGridOptions));
      }

      Hotkeys.setup();
    }

    return this.grid.gridView;
  }

  gridDidUpdateHandler() {
    const data = this.grid.gridView.dataSource.rawData;
    setTimeout(() => {
      if (data) {
        const count = data['@ns.unfiltered.count'];
        this.setCreateBtnDisabled(this.shouldDisableCreateBtn(count));
      }
    });
  }

  setPageNumberToState(number) {
    state.set('pgridPageNumber', _.defaults({
      [this.gridName]: number,
    }, state.get('pgridPageNumber') || {}));
  }

  setCreateBtnDisabled(shouldDisableCreateBtn) {
    if (shouldDisableCreateBtn) {
      $('#button-adgroup-create').attr('disabled', 'disabled');
    } else {
      $('#button-adgroup-create').prop('disabled', false);
    }
  }

  shouldDisableCreateBtn(count) {
    const campaign = this.currentCampaign;
    const CurrentAccount = appConfig.get('CurrentAccount');
    const { IsReadOnlyUser } = permissions;
    // Disable create button when -
    // 1. Current campaign is smart shopping campaign and has one ad group
    // 2. Current campaign is LinkedIn campaign and has one ad group
    if (this.scope.campaignId != null && campaign &&
      (campaign.CampaignType === 'LinkedIn' || campaign.CampaignSubType === 'ShoppingSmartAds' || campaign.CampaignSubType === 12)) {
      return count >= 1;
    }
    return !!(IsReadOnlyUser || (CurrentAccount && CurrentAccount.Status === 32));
  }

  remove() {
    if (!this.preexecuteFlag && permissions.IsCampaignAdGroupCopyPasteEnabled && this.grid.config.name === 'summary_tab_ad_group') {
      Hotkeys.reset();
    }
    this.gridRemoved = true;
    this.setPageNumberToState(0);
    return super.remove();
  }

  renderTrunk() {
    if (this.isForVNext) {
      this.filterService.initializeCombinedFilterPromise.then(() => {
        if (!this.gridRemoved) {
          updateGridQuery(
            this.grid.gridView,
            this.filterService.getCombinedFilter(), true
          );
        }
      });
    }
    super.renderTrunk();
  }

  get events() {
    const events = {
      'click .labels-cell-container #see-all': 'onLabelsRowClicked',
      'click .labels-cell-item': (event) => {
        const key = this.grid.gridView.keyOfElement(event.target);
        const targetEntity = this.grid.gridView.itemWithKey(key);
        filterOnLabelClick(
          event,
          targetEntity.Labels,
          this.grid.gridView,
          this.filterService,
          this.isMCCLabelEnabled
        );
      },
      'click .manage-labels-button': () => navigateToManagePage(this.router),
      'click .bidlandscape_icon': 'showBidLandscape',
      'click .quality-score-detail': 'showQualityScoreDetails',
      'click .delivery_status_with_reject_reasons': 'showRejectionReason',
      'mouseenter .dashed-date-entry': event => renderCampaignManagedDateTooltip({
        event, view: this.grid.gridView, i18n, router: this.router, scope: this.scope,
      }),
      'mouseleave .dashed-date-entry': event => hideCampaignManagedDateTooltip({ event, view: this.grid.gridView }),
    };

    if (this.isForVNext) {
      events['mouseenter .delivery_status_with_reject_reasons'] = permissions.IsDeferredHoveringPopupEnabled ? _.partial(handleShowRejectionReasonEnter, this, this.showRejectionReason) : 'showRejectionReason';
      events['mouseleave .delivery_status_with_reject_reasons'] = permissions.IsDeferredHoveringPopupEnabled ? _.partial(handleShowRejectionReasonLeave, null, _.noop) : null;
      events['mouseleave .grid-column-Delivery'] = 'hideRejectionReason';

      events['click .editorial_delivery_status_with_inline_appeals'] = 'showEditorialRejectionReason';
      events['mouseenter .editorial_delivery_status_with_inline_appeals'] = 'showEditorialRejectionReason';
    }

    return events;
  }

  getTargetEnity(event) {
    const key = this.grid.gridView.keyOfElement(event.target);

    return this.grid.gridView.itemWithKey(key);
  }

  onLabelsRowClicked(event) {
    const targetEntity = this.getTargetEnity(event);
    this.grid.gridView.trigger('willShowLabelsPopup');

    showLabelsPopup({
      labels: targetEntity.Labels,
      entityType: 'AdGroup',
      entityName: targetEntity.Name,
      container: this.isForVNext ? $('.grid-and-disclaim-container') : this.$el,
      event,
      router: this.router,
      i18n,
      grid: this.grid,
      filterService: this.filterService,
      isMCCLabelEnabled: this.isMCCLabelEnabled,
    });
    if (this.isForVNext) {
      event.preventDefault();
    }
  }

  showEditorialRejectionReason(event) {
    const key = this.grid.gridView.keyOfElement(event.target);
    const selectedItem = this.grid.gridView.itemWithKey(key);
    const editorialStatus = $(event.target).parent('.delivery-info').data('status');
    const attributeId = $(event.target).data('entityid');
    const valueLevel = $(event.target).parent('.delivery-info').data('valuelevel');
    const activity = instrumentation && instrumentation.editorial && instrumentation.editorial.create().toggleCountryList.create();

    showRejectionReasons({
      event,
      i18n,
      deliveryStatusDetails: [editorialStatus],
      getCustomTemplate: _.partial(getEditorialReasonsTemplate, {
        event,
        ajaxOptions: {
          host: appConfig.get('odata.url'),
          path: appConfig.get('odata.multipart_path'),
          oDataToken: appConfig.get('odata.token'),
          params: {
            campaign_id: selectedItem.Campaign.Id,
            account_id: appConfig.get('CurrentAccount').Id,
            customer_id: appConfig.get('CurrentCustomer').Id,
            adgroup_id: selectedItem.Id,
          },
          sessionId: appConfig.get('Omniture').SessionId,
        },
        instrumentation,
        i18n,
        attributeId,
        deliveryStatus: editorialStatus,
        valueLevel,
        permissions,
        commonI18n,
      }),
      useCustomTemplate: () => true,
      popoverId: this.popoverId,
      isForVNext: this.isForVNext,
      view: this.grid.gridView,
      afterRender: popup => registerCountryListEvent(popup.$el, activity, { id: selectedItem.Id, entity: 'ad group customizer attribute' }),
    });
  }

  showRejectionReason(event) {
    const actionLinks = [];
    if (this.isForVNext) {
      const entity = this.getTargetEnity(event);
      if (entity &&
          entity.DeliveryStatusDetails &&
          _.intersection(entity.DeliveryStatusDetails, ['NoEligibleAds', 'NoEligibleKeywords']).length > 0) {
        const resolveURL = (url) => {
          const uri = new URI(url);
          appendScopeParameter(uri, this.scope);
          uri.addQuery('campaignId', entity.Campaign.Id);
          uri.addQuery('adGroupId', entity.Id);
          uri.addQuery('isOpenCreatePanel', true);
          return uri.toString();
        };

        if (entity.DeliveryStatusDetails.includes('NoEligibleAds')) {
          const url = this.router.getRoutePathById('ads');
          actionLinks.NoEligibleAds = resolveURL(url);
        }
        if (entity.DeliveryStatusDetails.includes('NoEligibleKeywords')) {
          const url = this.router.getRoutePathById('keywords');
          actionLinks.NoEligibleKeywords = resolveURL(url);
        }
      }

      if (entity && entity.DeliveryStatusDetails &&
        entity.DeliveryStatusDetails.includes('ShoppingCampaignNoOffersPresent')) {
        // use all site map to get merchant center url for different scopes
        const url = getRoutePathById(allSiteMap, 'merchant-center');
        const uri = new URI(url);
        appendScopeParameter(uri, this.scope);
        actionLinks.ShoppingCampaignNoOffersPresent = uri.toString();
      }
    }

    const view = new DeliveryStatusPopupFactory({ grid: this.grid, i18n });
    view.showRejectionPopup({
      event,
      actionLinks,
      containerSelector: this.rejectionPopupContainerSelector,
      view: this,
      popoverId: this.popoverId,
      instrumentation,
      inContextRepairRecommedationType: 'adGroup',
    });
  }

  hideRejectionReason(e) {
    if ($(e.relatedTarget).closest(`#${this.popoverId}`).length === 0) {
      this.grid.gridView.trigger('closeDeliveryStatusPopup');
    }
  }

  showBidLandscape(event) {
    const targetEntity = this.getTargetEnity(event);

    const initializer = this.isForVNext
      ? () => getAdGroupBidLandscapeInitializerWebpack(
        true,
        this.deps.bidLandscapeDependencies
      )
      : getAdGroupBidLandscapeInitializer;

    initializer()
      .then(({ openAdGroupBidLandscape }) =>
        openAdGroupBidLandscape({
          event,
          scope: this.scope,
          adgroup: targetEntity,
          applyCallback: (updates) => {
            const newAdGroup = _.defaults({ ...updates }, targetEntity);

            this.grid.gridView.trigger('didEdit', newAdGroup);
          },
          applyBlockerEl: this.grid.gridView.$el.parent(),
          container: this.$el,
          isForVNext: this.isForVNext,
        }));
  }

  showQualityScoreDetails(event) {
    const targetEntity = this.getTargetEnity(event);

    showQualityScoreDetail(event, targetEntity.QualityScore, 'qsContainer', legacyGlobalI18n); // qsContainer is the id from qsContainerTemplate
  }

  get className() { return 'ad-groups-grid'; }
}
